import { useRef, useState, useEffect, useCallback } from 'react';
import { useVastPlayer }                 from '../hooks/useVastPlayer';
import { MediaSourceHandler, MP4_TYPES } from '../lib/MediaSourceHandler';
import { VastTracker }                   from '../lib/VastTracker';
import { AdEvents }                      from '../lib/AdEvents';
import AdBlockedNotice                   from './AdBlockedNotice/AdBlockedNotice';
import Footer                            from './Footer/Footer';
import MuteIcon                          from './icons/MuteIcon';
import UnMuteIcon                        from './icons/UnmuteIcon';
import './VastOutstreamPlayer.css';

export default function VastOutstreamPlayer({
  vastUrl,
  vastXml,
  muted: initialMuted = true,
  autoplay = true,
  viewabilityThreshold = 0.5,
  pauseWhenHidden = true,
  skipDelay: skipProp = null,
  showCloseButton = false,
  // article data from iframe payload (price, currency, url)
  article = null,
  // parent iframe origin for postMessage
  parentOrigin = null,
  // telemetry emitter
  emitter = null,
  // legacy prop callbacks
  onAdReady, onAdStart, onAdComplete, onAdSkip, onAdClose, onAdError,
}) {
  const videoRef    = useRef(null);
  const rootRef     = useRef(null);
  const isStarted    = useRef(false);
  const isEnding     = useRef(false);
  const userPausedRef = useRef(false);
  const formatRef   = useRef(null);
  const allFilesRef = useRef([]);
  const durationMs  = useRef(0);

  const VIEW_MS      = 2000;
  const viewTimerRef = useRef(null);
  const viewableRef  = useRef(false);
  const viewFiredRef = useRef(false);

  const [currentTimeMs,  setCurrentTimeMs]  = useState(0);
  const [durationMsState, setDurationMsState] = useState(0);
  const [isMuted,        setIsMuted]        = useState(initialMuted);
  const [isPaused,       setIsPaused]       = useState(false);
  const [skipCountdown,  setSkipCountdown]  = useState(null);
  const [allAdsDone,     setAllAdsDone]     = useState(false);
  const [showFooter,     setShowFooter]     = useState(false);
  const [showAdBlockedNotice, setShowAdBlockedNotice] = useState(false);
  const [needsGesture,   setNeedsGesture]   = useState(false);

  const { status, vastData, error, trackerRef } = useVastPlayer({ vastUrl, vastXml });
  const skipDelay = skipProp ?? vastData?.linear?.skipoffset ?? null;

  // ── Helper: build base event payload ──────────────────────────────────────
  const baseEvent = useCallback((extra = {}) => {
    const video = videoRef.current;
    const ctMs  = Math.round((video?.currentTime || 0) * 1000);
    const durMs = durationMs.current || 0;
    return {
      adId:          vastData?.id         || '',
      adTitle:       vastData?.adTitle    || '',
      advertiser:    vastData?.advertiser || '',
      durationMs:    durMs,
      currentTimeMs: ctMs,
      progress:      durMs ? Math.min(100, (ctMs / durMs) * 100) : 0,
      timestamp:     new Date().toISOString(),
      ...extra,
    };
  }, [vastData]);

  // ── Helper: emit + call legacy prop callback ───────────────────────────────
  const fire = useCallback((type, extra, legacyCb) => {
    const payload = baseEvent(extra);
    emitter?.emit(type, payload);
    legacyCb?.(payload);
  }, [emitter, baseEvent]);

  // ── VAST requested ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!vastUrl && !vastXml) return;
    emitter?.emit(AdEvents.AD_REQUESTED, {
      adId: '', adTitle: '', advertiser: '',
      durationMs: 0, currentTimeMs: 0, progress: 0,
      timestamp: new Date().toISOString(),
    });
  }, [vastUrl, vastXml]); // eslint-disable-line

  // ── Status callbacks ───────────────────────────────────────────────────────
  useEffect(() => {
    if (status === 'ready') onAdReady?.();
    if (status === 'error' && error) {
      setShowAdBlockedNotice(true);
      fire(AdEvents.ERROR, { code: error.vastCode, message: error.message },
        p => onAdError?.({ code: p.code, message: p.message })
      );
    }
  }, [status]); // eslint-disable-line

  // ── Effect 1: Media attach + set durationMs ───────────────────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!vastData || !video) return;
    video.muted = initialMuted;
    const allFiles = vastData.linear.mediaFiles;
    allFilesRef.current = allFiles;

    let sel;
    try { sel = MediaSourceHandler.select(allFiles); }
    catch (e) {
      setShowAdBlockedNotice(true);
      fire(AdEvents.ERROR, { code: 403, message: e.message });
      onAdError?.({ code: 403, message: e.message });
      return;
    }
    formatRef.current = sel.format;
    trackerRef.current = new VastTracker(vastData, { assetUri: sel.file.url, podSequence: -1 });

    MediaSourceHandler.attach(video, sel.file, sel.format, allFiles, () => {
      setShowAdBlockedNotice(true);
      fire(AdEvents.ERROR, { code: 403, message: 'No playable media after HLS fallback' });
    });

    const onLoadedMeta = () => {
      durationMs.current = Math.round((video.duration || vastData.linear.duration || 0) * 1000);
      setDurationMsState(durationMs.current);
      trackerRef.current?.fireLoaded();
      fire(AdEvents.LOADED);
    };
    video.addEventListener('loadedmetadata', onLoadedMeta, { once: true });

    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMeta);
      MediaSourceHandler.detach(video);
    };
  }, [vastData]); // eslint-disable-line

  // ── Effect 2: Viewability / autoplay + ViewableImpression ─────────────────
  useEffect(() => {
    const root = rootRef.current;
    if (!autoplay || !root || !vastData) return;

    const observer = new IntersectionObserver(([entry]) => {
      const video   = videoRef.current;
      const visible = entry.intersectionRatio >= viewabilityThreshold;
      viewableRef.current = visible;

      if (!viewFiredRef.current) {
        clearTimeout(viewTimerRef.current);
        if (visible) {
          viewTimerRef.current = setTimeout(() => {
            if (viewableRef.current && !viewFiredRef.current) {
              viewFiredRef.current = true;
              trackerRef.current?.fireViewableImpression('viewable');
              fire(AdEvents.VIEWABLE_IMPRESSION, { outcome: 'viewable' });
            }
          }, VIEW_MS);
        } else if (isStarted.current) {
          viewFiredRef.current = true;
          trackerRef.current?.fireViewableImpression('notViewable');
          fire(AdEvents.VIEWABLE_IMPRESSION, { outcome: 'notViewable' });
        }
      }

      if (!video) return;
      if (visible && !isStarted.current) {
        isStarted.current = true;
        trackerRef.current?.fireCreativeView();
        onAdStart?.();
        video.play().catch(() => { video.muted = true; video.play().catch(() => setNeedsGesture(true)); });
      } else if (!visible && pauseWhenHidden && !video.paused) {
        video.pause();
      } else if (visible && isStarted.current && video.paused && !isEnding.current && !userPausedRef.current) {
        video.play().catch(() => {});
      }
    }, { threshold: [viewabilityThreshold] });

    observer.observe(root);
    return () => {
      observer.disconnect();
      clearTimeout(viewTimerRef.current);
      if (!viewFiredRef.current) {
        viewFiredRef.current = true;
        trackerRef.current?.fireViewableImpression('viewUndetermined');
        fire(AdEvents.VIEWABLE_IMPRESSION, { outcome: 'viewUndetermined' });
      }
    };
  }, [vastData, autoplay, viewabilityThreshold, pauseWhenHidden]); // eslint-disable-line

  // ── Effect 3: Fullscreen ──────────────────────────────────────────────────
  useEffect(() => {
    const onChange = () => {
      const isFS = !!document.fullscreenElement;
      trackerRef.current?.fireEvent(isFS ? 'fullscreen' : 'exitFullscreen');
      fire(isFS ? AdEvents.FULLSCREEN : AdEvents.EXIT_FULLSCREEN);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []); // eslint-disable-line

  // ── Effect 4: Skip countdown ──────────────────────────────────────────────
  useEffect(() => {
    if (skipDelay !== null && skipDelay !== undefined)
      setSkipCountdown(Math.ceil(skipDelay));
  }, [skipDelay]);

  // ── Effect 5: Patch tracker to forward quartile events to emitter ──────────
  useEffect(() => {
    const tracker = trackerRef.current;
    if (!tracker || !emitter) return;
    const origFireEvent = tracker.fireEvent.bind(tracker);
    tracker.fireEvent = (name) => {
      origFireEvent(name);
      const map = {
        start:          AdEvents.STARTED,
        firstQuartile:  AdEvents.FIRST_QUARTILE,
        midpoint:       AdEvents.MIDPOINT,
        thirdQuartile:  AdEvents.THIRD_QUARTILE,
        complete:       AdEvents.COMPLETE,
        skip:           AdEvents.SKIPPED,
        pause:          AdEvents.PAUSED,
        resume:         AdEvents.RESUMED,
        mute:           AdEvents.MUTED,
        unmute:         AdEvents.UNMUTED,
        fullscreen:     AdEvents.FULLSCREEN,
        exitFullscreen: AdEvents.EXIT_FULLSCREEN,
      };
      if (map[name]) fire(map[name]);
    };
  }, [trackerRef.current, emitter, fire]); // eslint-disable-line

  // ── Video event handlers ──────────────────────────────────────────────────
  const onTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const durSec = v.duration || vastData?.linear?.duration || 0;
    const ct = v.currentTime;
    if (durSec > 0) {
      durationMs.current = Math.round(durSec * 1000);
      setDurationMsState(durationMs.current);
    }
    trackerRef.current?.trackTime(ct, durSec);
    if (durSec) {
      setCurrentTimeMs(Math.round(ct * 1000));
    }
    if (skipDelay !== null && skipDelay !== undefined)
      setSkipCountdown(skipDelay - ct > 0 ? Math.ceil(skipDelay - ct) : 0);
  }, [vastData, skipDelay, trackerRef]);

  const onEnded = useCallback(() => {
    isEnding.current = true;
    setAllAdsDone(true);
    try {
      window.parent.postMessage({ type: 'ad:complete' }, parentOrigin || '*');
    } catch {}
    fire(AdEvents.COMPLETE);
    onAdComplete?.();
  }, [fire, onAdComplete, parentOrigin]);

  const onPlay = useCallback(() => {
    trackerRef.current?.fireEvent('resume');
    setShowFooter(true);
    setNeedsGesture(false);
    setIsPaused(false);
    if (!isStarted.current) {
      isStarted.current = true;
      fire(AdEvents.STARTED);
      onAdStart?.();
    } else {
      fire(AdEvents.RESUMED);
    }
  }, [trackerRef, fire, onAdStart]);

  const onPause = useCallback(() => {
    if (!isEnding.current) {
      setIsPaused(true);
      trackerRef.current?.fireEvent('pause');
      fire(AdEvents.PAUSED);
    }
  }, [trackerRef, fire]);

  const onVolume = useCallback(() => {
    const v = videoRef.current; if (!v) return;
    const m = v.muted || v.volume === 0;
    setIsMuted(m);
    trackerRef.current?.fireEvent(m ? 'mute' : 'unmute');
    fire(m ? AdEvents.MUTED : AdEvents.UNMUTED);
  }, [trackerRef, fire]);

  const onVideoError = useCallback(() => {
    const hasMp4 = allFilesRef.current.some(m => MP4_TYPES.has(m.type));
    if (!hasMp4 || formatRef.current === 'mp4') {
      setShowAdBlockedNotice(true);
      trackerRef.current?.fireError(402);
      fire(AdEvents.ERROR, { code: 402, message: 'Video playback error' });
      onAdError?.({ code: 402, message: 'Video playback error' });
    }
  }, [trackerRef, fire, onAdError]);

  const onVideoClick = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      userPausedRef.current = false;
      v.play().catch(() => {});
    } else {
      userPausedRef.current = true;
      v.pause();
    }
  }, []);

  const toggleMuted = useCallback(() => {
    const v = videoRef.current; if (!v) return;
    v.muted = !v.muted;
  }, []);

  const onSkip = useCallback(() => {
    trackerRef.current?.fireEvent('skip');
    fire(AdEvents.SKIPPED);
    onAdSkip?.();
  }, [trackerRef, fire, onAdSkip]);

  const onClose = useCallback(() => {
    trackerRef.current?.fireEvent('closeLinear');
    fire(AdEvents.CLOSED);
    onAdClose?.();
  }, [trackerRef, fire, onAdClose]);

  const manualStart = useCallback(() => {
    const v = videoRef.current; if (!v) return;
    v.play().catch(() => {});
    setNeedsGesture(false);
  }, []);

  // ── Render: loading ────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div className="adonly-loading">
        <div className="adonly-loading-card">
          <div className="adonly-spinner" />
          <span className="adonly-loading-text">Loading ad…</span>
        </div>
      </div>
    );
  }

  if (status === 'idle') return null;

  // ── Render: error (no player, just blocked notice) ─────────────────────────
  if (status === 'error') {
    return (
      <AdBlockedNotice
        variant="fullscreen"
        message="Please disable your ad blocker and refresh the page to view this ad."
      />
    );
  }

  // ── Render: player ─────────────────────────────────────────────────────────
  return (
    <div ref={rootRef} className="adonly-overlay" role="dialog" aria-modal="true">
      <div className="adonly-card-wrapper">
        <div className="adonly-card">
          <div className="adonly-stage">
            <video
              ref={videoRef}
              className="adonly-video"
              playsInline
              onTimeUpdate={onTimeUpdate}
              onEnded={onEnded}
              onPlay={onPlay}
              onPause={onPause}
              onVolumeChange={onVolume}
              onError={onVideoError}
              onClick={onVideoClick}
            />

            {/* Mute / unmute button */}
            {!showAdBlockedNotice && (
              <button
                type="button"
                className="adonly-mute"
                onClick={toggleMuted}
                aria-pressed={isMuted}
                aria-label={isMuted ? 'Unmute ad' : 'Mute ad'}
              >
                {isMuted ? <MuteIcon /> : <UnMuteIcon />}
              </button>
            )}

            {/* Close button */}
            {showCloseButton && !showAdBlockedNotice && (
              <button type="button" className="adonly-close" onClick={onClose} aria-label="Close ad">
                ✕
              </button>
            )}

            {/* Skip button */}
            {skipDelay !== null && skipCountdown !== null && !showAdBlockedNotice && (
              <button
                type="button"
                className={`adonly-skip${skipCountdown === 0 ? ' ready' : ''}`}
                onClick={skipCountdown === 0 ? onSkip : undefined}
                aria-label={skipCountdown > 0 ? `Skip in ${skipCountdown}s` : 'Skip ad'}
              >
                {skipCountdown > 0 ? `Skip in ${skipCountdown}s` : 'Skip Ad ›'}
              </button>
            )}

            {/* Manual play CTA (autoplay blocked) */}
            {needsGesture && !showAdBlockedNotice && (
              <button type="button" className="adonly-cta" onClick={manualStart}>
                Play Ad
              </button>
            )}

            {/* Ad blocked notice inside stage */}
            {showAdBlockedNotice && <AdBlockedNotice variant="stage" />}
          </div>

          {/* Footer — shown once ad starts playing */}
          {showFooter && !showAdBlockedNotice && (
            <Footer
              allAdsDone={allAdsDone}
              setAllAdsDone={setAllAdsDone}
              price={article?.price}
              currency={article?.currency}
              currentTime={currentTimeMs}
              duration={durationMsState}
            />
          )}
        </div>
      </div>
    </div>
  );
}
